<template>
  <div class="details-content">
    <h2
      v-if="primaryGameName"
      class="game-name-heading"
    >
      {{ primaryGameName }}
    </h2>

    <!-- Transaction summary -->
    <div class="transaction-header-bar">
      <div class="header-info-list">
        <div class="info-row">
          <span class="info-label">{{ $t('wagers.transId') }}:</span>
          <span class="info-value info-value-copy">
            {{ transaction.transaction.transaction_id }}
            <button
              type="button"
              class="copy-button"
              :aria-label="$t('transactionDetails.copyToClipboard')"
              @click="copyToClipboard(String(transaction.transaction.transaction_id))"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <rect
                  x="5"
                  y="5"
                  width="9"
                  height="9"
                  rx="1"
                  stroke="currentColor"
                  stroke-width="1.25"
                />
                <path
                  d="M4 11H3C2.44772 11 2 10.5523 2 10V3C2 2.44772 2.44772 2 3 2H10C10.5523 2 11 2.44772 11 3V4"
                  stroke="currentColor"
                  stroke-width="1.25"
                />
              </svg>
            </button>
          </span>
        </div>
        <div class="info-row">
          <span class="info-label">{{ $t('wagers.date') }}:</span>
          <span class="info-value">{{ formatHeaderDate(transaction.transaction.date) }}</span>
        </div>
        <div class="info-row">
          <span class="info-label">{{ $t('wagers.reference') }}:</span>
          <span class="info-value info-value-copy reference-wrap">
            {{ transaction.transaction.reference }}
            <button
              type="button"
              class="copy-button"
              :aria-label="$t('transactionDetails.copyToClipboard')"
              @click="copyToClipboard(transaction.transaction.reference)"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <rect
                  x="5"
                  y="5"
                  width="9"
                  height="9"
                  rx="1"
                  stroke="currentColor"
                  stroke-width="1.25"
                />
                <path
                  d="M4 11H3C2.44772 11 2 10.5523 2 10V3C2 2.44772 2.44772 2 3 2H10C10.5523 2 11 2.44772 11 3V4"
                  stroke="currentColor"
                  stroke-width="1.25"
                />
              </svg>
            </button>
          </span>
        </div>
        <div class="info-row">
          <span class="info-label">{{ $t('wagers.description') }}:</span>
          <span class="info-value">{{ $t('wagers.wager') }}</span>
        </div>
        <div class="info-row">
          <span class="info-label">{{ $t('wagers.drawsPlayed') }}:</span>
          <span class="info-value">{{ drawCount }}</span>
        </div>
        <div class="info-row">
          <span class="info-label">{{ $t('wagers.totalBetAmount') }}:</span>
          <span class="info-value font-bold">
            {{ formatCurrency(transaction.transaction.total_cost, transaction.transaction.currency_symbol) }}
          </span>
        </div>
        <div class="info-row">
          <span class="info-label">{{ $t('wagers.totalReturn') }}:</span>
          <span class="info-value font-bold">
            {{ formatSlipTotalReturn() }}
          </span>
        </div>
        <div class="info-row">
          <span class="info-label">{{ $t('wagers.status') }}:</span>
          <span :class="['info-value', 'status-text', formatStatusClass(getTransactionStatus(), transaction.transaction.total_winnings)]">
            {{ formatStatusLabel(getTransactionStatus(), transaction.transaction.total_winnings) }}
          </span>
        </div>
      </div>
    </div>

    <section
      v-if="drawCount > 0"
      class="draws-section"
    >
      <h3 class="draws-section-title">
        {{ $t('transactionDetails.drawsSection', { count: drawCount }) }}
      </h3>

      <div
        v-for="entry in flattenedDraws"
        :key="drawKey(entry.gameKey, entry.game)"
        class="game-draw-section"
      >
        <div class="draw-header-bar">
          <div class="draw-header-main">
            <span class="draw-index-badge">{{ entry.drawIndex }}</span>
            <span class="game-draw-title">
              {{ entry.game.name }} - {{ formatDrawDate(entry.game.draw_date) }}
              <span
                v-if="entry.game.event_cancelled === 'yes'"
                class="event-cancelled-badge"
              >CANCELLED</span>
            </span>
          </div>
          <span
            :class="['draw-status-text', formatStatusClass(getDrawStatus(entry.game), getGameTotalWinnings(entry.game))]"
          >
            {{ formatStatusLabel(getDrawStatus(entry.game), getGameTotalWinnings(entry.game)) }}
          </span>
        </div>

        <div class="draw-summary-rows">
          <div class="summary-row-item">
            <span class="summary-label">{{ $t('transactionDetails.drawId') }}:</span>
            <span class="summary-value">{{ entry.game.event_id || entry.game.draw_id }}</span>
          </div>
          <div class="summary-row-item">
            <span class="summary-label">{{ $t('transactionDetails.results') }}:</span>
            <span class="summary-value">
              <span
                v-if="getWinningNumbers(entry.game).length > 0"
                class="results-numbers-inline"
              >
                <span
                  v-for="(number, index) in getWinningNumbers(entry.game)"
                  :key="index"
                  :class="['number-ball-item', { 'bonus': isWinningBonusNumber(index, entry.game) }]"
                >
                  {{ number }}
                </span>
              </span>
              <span
                v-else-if="entry.game.has_results && isTransactionSettled"
                class="no-results-text"
              >
                {{ $t('wagers.pendingResults') }}
              </span>
              <span
                v-else
                class="no-results-text"
              >{{ $t('wagers.tbc') }}</span>
            </span>
          </div>
          <div class="summary-row-item">
            <span class="summary-label">{{ $t('wagers.totalBet') }}:</span>
            <span class="summary-value font-bold">
              {{ formatDrawTotalBet(entry.game) }}
            </span>
          </div>
          <div class="summary-row-item">
            <span class="summary-label">{{ $t('wagers.totalReturn') }}:</span>
            <span class="summary-value font-bold">
              {{ formatDrawTotalReturn(entry.game) }}
            </span>
          </div>
          <button
            type="button"
            class="summary-row-item bets-accordion-trigger"
            :aria-expanded="isDrawExpanded(entry)"
            @click="toggleDrawExpanded(entry)"
          >
            <span class="summary-label bets-accordion-label">
              {{ $t('transactionDetails.yourBetsInDraw', { count: entry.game.bets.length }) }}
            </span>
            <svg
              class="accordion-chevron"
              :class="{ 'accordion-chevron--expanded': isDrawExpanded(entry) }"
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path
                d="M4 6L8 10L12 6"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </button>
        </div>

        <div
          v-show="isDrawExpanded(entry)"
          class="bets-table-container"
        >
          <!-- Desktop Table -->
          <div class="desktop-only">
            <table class="bets-data-table">
              <colgroup>
                <col class="col-bet-id">
                <col class="col-your-bet">
                <col class="col-bet-type">
                <col class="col-odds">
                <col class="col-bet-amount">
                <col class="col-potential">
                <col class="col-actual">
                <col class="col-status">
              </colgroup>
              <tbody
                v-for="(bet, betIndex) in entry.game.bets"
                :key="`${bet.bet_id}-${betIndex}`"
                class="bet-group"
              >
                <tr class="bet-group-label-row">
                  <td
                    colspan="8"
                    class="bet-group-label-cell"
                  >
                    <div class="bet-id-cell">
                      <span class="draw-index-badge bet-letter-badge">{{ betLetterLabel(betIndex) }}</span>
                      <span class="bet-id-text">{{ $t('transactionDetails.betId') }}:{{ bet.bet_id }}</span>
                    </div>
                  </td>
                </tr>
                <tr class="bet-group-header-row">
                  <th class="col-text">
                    {{ $t('transactionDetails.betId') }}
                  </th>
                  <th class="col-text col-your-bet">
                    {{ $t('transactionDetails.yourBet') }}
                  </th>
                  <th class="col-text">
                    {{ $t('transactionDetails.betType') }}
                  </th>
                  <th class="col-numeric">
                    {{ $t('transactionDetails.odds') }}
                  </th>
                  <th class="col-numeric">
                    {{ $t('transactionDetails.betAmount') }}
                  </th>
                  <th class="col-numeric">
                    {{ $t('transactionDetails.potentialReturns') }}
                  </th>
                  <th class="col-numeric">
                    {{ $t('transactionDetails.actualReturns') }}
                  </th>
                  <th class="col-status">
                    {{ $t('wagers.status') }}
                  </th>
                </tr>
                <tr class="bet-group-data-row">
                  <td class="col-text font-bold">
                    {{ bet.bet_id }}
                  </td>
                  <td class="col-text col-your-bet">
                    <div class="bet-details-info">
                      <span class="market-name-label">{{ bet.market_name }}</span>
                      <span
                        v-if="bet.market_text"
                        class="market-text-label"
                      >{{ bet.market_text }}</span>
                    </div>
                    <div
                      v-if="hasBetNumbers(bet)"
                      class="selected-numbers-row"
                    >
                      <template v-for="(numbers, index) in bet.bet_numbers">
                        <template v-if="numbers.length > 0">
                          <span
                            v-for="number in numbers"
                            :key="`${index}-${number}`"
                            :class="['number-ball-small', { 'bonus': index === 1 }]"
                          >{{ number }}</span>
                        </template>
                      </template>
                    </div>
                  </td>
                  <td class="col-text">
                    {{ bet.betType }}
                  </td>
                  <td class="col-numeric">
                    {{ bet.odds }}
                  </td>
                  <td class="col-numeric">
                    {{ formatCurrency(bet.bet_cost, transaction.transaction.currency_symbol) }}
                  </td>
                  <td class="col-numeric">
                    {{ formatPotentialReturns(bet, entry.game) }}
                  </td>
                  <td class="col-numeric">
                    {{ formatActualReturns(bet, entry.game) }}
                  </td>
                  <td class="col-status">
                    <span :class="['bet-status-label', formatStatusClass(getBetStatus(bet, entry.game), bet.winnings)]">
                      {{ formatStatusLabel(getBetStatus(bet, entry.game), bet.winnings) }}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Mobile Cards -->
          <div class="mobile-only">
            <div
              v-for="(bet, betIndex) in entry.game.bets"
              :key="`${bet.bet_id}-${betIndex}`"
              class="bet-item-card-mobile"
            >
              <div class="bet-card-header">
                <div class="bet-id-cell">
                  <span class="draw-index-badge bet-letter-badge">{{ betLetterLabel(betIndex) }}</span>
                  <span class="bet-id-text">{{ $t('transactionDetails.betId') }}:{{ bet.bet_id }}</span>
                </div>
                <span
                  :class="['bet-card-status', formatStatusClass(getBetStatus(bet, entry.game), bet.winnings)]"
                >
                  {{ formatStatusLabel(getBetStatus(bet, entry.game), bet.winnings) }}
                </span>
              </div>

              <div class="bet-row-item">
                <span class="label">{{ $t('transactionDetails.yourBet') }}:</span>
                <span class="value bet-outcome-value">
                  <span class="market-name-label">{{ bet.market_name }}</span>
                  <span
                    v-if="bet.market_text"
                    class="market-text-label"
                  >{{ bet.market_text }}</span>
                  <span
                    v-if="hasBetNumbers(bet)"
                    class="selected-numbers-row"
                  >
                    <template v-for="(numbers, index) in bet.bet_numbers">
                      <template v-if="numbers.length > 0">
                        <span
                          v-for="number in numbers"
                          :key="`${bet.bet_id}-${index}-${number}`"
                          :class="['number-ball-small', { 'bonus': index === 1 }]"
                        >{{ number }}</span>
                      </template>
                    </template>
                  </span>
                </span>
              </div>

              <div class="bet-row-item">
                <span class="label">{{ $t('transactionDetails.betType') }}:</span>
                <span class="value">{{ bet.betType }}</span>
              </div>

              <div class="bet-row-item">
                <span class="label">{{ $t('transactionDetails.odds') }}:</span>
                <span class="value">{{ bet.odds }}</span>
              </div>

              <div class="bet-row-item">
                <span class="label">{{ $t('transactionDetails.betAmount') }}:</span>
                <span class="value">
                  {{ formatCurrency(bet.bet_cost, transaction.transaction.currency_symbol) }}
                </span>
              </div>

              <div class="bet-row-item">
                <span class="label">{{ $t('transactionDetails.potentialReturns') }}:</span>
                <span class="value">
                  {{ formatPotentialReturns(bet, entry.game) }}
                </span>
              </div>

              <div class="bet-row-item">
                <span class="label">{{ $t('transactionDetails.actualReturns') }}:</span>
                <span class="value">
                  {{ formatActualReturns(bet, entry.game) }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <div class="details-footer">
      <button
        class="footer-rebet-button"
        :disabled="isRebetting"
        :aria-busy="isRebetting"
        @click="handleRebet"
      >
        {{ $t('wagers.rebet') }}
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import type { Game, TransactionDetail } from "@/stores/transactions";
import { formatCurrency } from "@/utils/currency";
import { useTheme } from "@/composables/useTheme";
import { useToast } from "@/composables/useToast";
import { copyTextToClipboard } from "@/utils/copyToClipboard";

const props = defineProps<{
  transaction: TransactionDetail;
  isRebetting?: boolean;
}>();

const { t } = useI18n();
const { showToast } = useToast();
const { currentThemeName } = useTheme();
const useWonLostLabels = computed(() => currentThemeName.value === 'wsb');

type FlatDraw = { gameKey: string; game: Game; drawIndex: number };

const flattenedDraws = computed((): FlatDraw[] => {
  const list: FlatDraw[] = [];
  let index = 1;
  for (const gameKey in props.transaction.games) {
    const games = props.transaction.games[gameKey] || [];
    for (const game of games) {
      list.push({ gameKey, game, drawIndex: index++ });
    }
  }
  return list;
});

const drawCount = computed(() => flattenedDraws.value.length);

const primaryGameName = computed(() => {
  const names = new Set(flattenedDraws.value.map((entry) => entry.game.name));
  if (names.size === 1) {
    return [...names][0];
  }
  return flattenedDraws.value[0]?.game.name ?? '';
});

const expandedDraws = ref<Record<string, boolean>>({});

const drawKey = (gameKey: string, game: Game) => `${gameKey}-${game.draw_id}`;

watch(
  flattenedDraws,
  (list) => {
    list.forEach((entry, idx) => {
      const key = drawKey(entry.gameKey, entry.game);
      if (!(key in expandedDraws.value)) {
        expandedDraws.value[key] = false;
      }
    });
  },
  { immediate: true },
);

const isDrawExpanded = (entry: FlatDraw) =>
  expandedDraws.value[drawKey(entry.gameKey, entry.game)] ?? false;

const toggleDrawExpanded = (entry: FlatDraw) => {
  const key = drawKey(entry.gameKey, entry.game);
  expandedDraws.value[key] = !expandedDraws.value[key];
};

const betLetterLabel = (index: number): string => {
  if (index < 0) return 'A';
  if (index < 26) return String.fromCharCode(65 + index);
  return String(index + 1);
};

const formatStatusLabel = (status: string, winnings: number | string = 0) => {
  if (!useWonLostLabels.value || String(status).toLowerCase() !== 'settled') {
    return status;
  }
  return parseFloat(String(winnings || 0)) > 0 ? t('wagers.won') : t('wagers.lost');
};

const formatStatusClass = (status: string, winnings: number | string = 0) => {
  if (!useWonLostLabels.value || String(status).toLowerCase() !== 'settled') {
    return String(status).toLowerCase();
  }
  return parseFloat(String(winnings || 0)) > 0 ? 'won' : 'lost';
};

const hasBetNumbers = (bet: any) => {
  if (!Array.isArray(bet?.bet_numbers)) return false;
  return bet.bet_numbers.some((group: any) => Array.isArray(group) && group.length > 0);
};

const emit = defineEmits<{
  (e: 'rebet'): void;
  (e: 'close'): void;
}>();

const isTransactionSettled = computed(() => {
  for (const gameKey in props.transaction.games) {
    const games = props.transaction.games[gameKey];
    for (const game of games) {
      for (const bet of game.bets) {
        const betStatus = getBetStatus(bet, game);
        if (betStatus !== 'Settled') {
          return false;
        }
      }
    }
  }
  return true;
});

const getTransactionStatus = () => {
  for (const gameKey in props.transaction.games) {
    const games = props.transaction.games[gameKey];
    for (const game of games) {
      if (game.event_cancelled === 'yes') {
        return 'Cancelled';
      }
      for (const bet of game.bets) {
        const betStatus = getBetStatus(bet, game);
        if (betStatus === 'Cancelled') {
          return 'Cancelled';
        }
        if (betStatus !== 'Settled') {
          return 'Open';
        }
      }
    }
  }
  return 'Settled';
};

const getDrawStatus = (game: Game) => {
  if (game.event_cancelled === 'yes') {
    return 'Cancelled';
  }
  for (const bet of game.bets) {
    const betStatus = getBetStatus(bet, game);
    if (betStatus === 'Cancelled') {
      return 'Cancelled';
    }
    if (betStatus !== 'Settled') {
      return 'Open';
    }
  }
  return 'Settled';
};

const handleRebet = () => {
  emit('rebet');
};

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const formatHeaderDate = (dateString: string) => {
  const date = new Date(dateString);
  const day = date.getDate();
  const month = MONTHS_SHORT[date.getMonth()];
  const year = date.getFullYear();
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  const seconds = date.getSeconds().toString().padStart(2, '0');
  return `${day} ${month} ${year}, ${hours}:${minutes}:${seconds}`;
};

const formatDrawDate = (dateString: string) => {
  const date = new Date(dateString);
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  const seconds = date.getSeconds().toString().padStart(2, '0');
  return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
};

const copyToClipboard = async (text: string) => {
  const copied = await copyTextToClipboard(text);
  if (copied) {
    showToast({
      title: t("transactionDetails.textCopied"),
      message: "",
      type: "success",
      duration: 2500,
      centered: true,
    });
    return;
  }
  showToast({
    title: t("common.error"),
    message: t("transactionDetails.copyFailed"),
    type: "error",
    duration: 4000,
    centered: true,
  });
};

const parseAmount = (value: string | number | null | undefined): number => {
  if (value === null || value === undefined || value === '') {
    return 0;
  }
  const parsed = typeof value === 'number' ? value : parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const getGameTotalBetCost = (game: Game): number =>
  game.bets.reduce((sum, bet) => sum + parseAmount(bet.bet_cost), 0);

const getGameTotalWinnings = (game: Game): number =>
  game.bets.reduce((sum, bet) => sum + parseAmount(bet.winnings), 0);

const isGameSettled = (game: Game): boolean => {
  if (game.event_cancelled === 'yes') {
    return true;
  }
  return game.bets.every((bet) => {
    const status = getBetStatus(bet, game);
    return status === 'Settled' || status === 'Cancelled';
  });
};

const formatSlipTotalReturn = () => {
  const status = getTransactionStatus();
  if (status === 'Open') {
    const winnings = parseAmount(props.transaction.transaction.total_winnings);
    if (winnings > 0) {
      return formatCurrency(winnings, props.transaction.transaction.currency_symbol);
    }
    return t('wagers.tbc');
  }
  return formatCurrency(
    props.transaction.transaction.total_winnings,
    props.transaction.transaction.currency_symbol,
  );
};

const formatDrawTotalBet = (game: Game) => {
  if (!isGameSettled(game)) {
    return t('wagers.tbc');
  }
  return formatCurrency(getGameTotalBetCost(game), props.transaction.transaction.currency_symbol);
};

const formatDrawTotalReturn = (game: Game) => {
  if (!isGameSettled(game)) {
    return t('wagers.tbc');
  }
  return formatCurrency(getGameTotalWinnings(game), props.transaction.transaction.currency_symbol);
};

const isBetOpen = (bet: any, game: Game) => {
  const status = getBetStatus(bet, game);
  return status === 'Open';
};

const formatPotentialReturns = (bet: any, game: Game) => {
  if (getBetStatus(bet, game) === 'Cancelled') {
    return formatCurrency(0, props.transaction.transaction.currency_symbol);
  }
  if (isBetOpen(bet, game)) {
    return t('wagers.tbc');
  }
  return formatCurrency(bet.bet_cost * bet.odds, props.transaction.transaction.currency_symbol);
};

const formatActualReturns = (bet: any, game: Game) => {
  if (getBetStatus(bet, game) === 'Cancelled') {
    return formatCurrency(0, props.transaction.transaction.currency_symbol);
  }
  if (isBetOpen(bet, game)) {
    return t('wagers.tbc');
  }
  return bet.winnings
    ? formatCurrency(bet.winnings, props.transaction.transaction.currency_symbol)
    : formatCurrency(0, props.transaction.transaction.currency_symbol);
};

const getBetStatus = (bet: any, game: any) => {
  if (game.event_cancelled === "yes") {
    return "Cancelled";
  }
  if (bet.cancelled === true) {
    return "Cancelled";
  }
  return bet.status;
};

const getWinningNumbers = (game: any) => {
  if (game?.results && Array.isArray(game.results) && game.results.length > 0) {
    return game.results.flat();
  }
  return [];
};

const isWinningBonusNumber = (index: number, game: any) => {
  if (game?.results && Array.isArray(game.results) && game.results.length > 1) {
    const mainNumbers = game.results[0] || [];
    return index >= mainNumbers.length;
  }
  return false;
};
</script>

<script lang="ts">
export default {
  name: 'TransactionDetails',
}
</script>

<style scoped>
.details-content {
  padding: var(--wagers-modal-body-padding, 24px);
  width: 100%;
  min-height: 100%;
  box-sizing: border-box;
  font-family: var(--typography-font-family, 'Ubuntu', sans-serif);
  background: var(--wagers-detail-page-bg);
  color: var(--wagers-detail-value-text);
}

.game-name-heading {
  margin: 0 0 16px;
  font-size: 16px;
  font-weight: 700;
  color: var(--wagers-detail-value-text);
  line-height: 1.3;
}

/* Transaction summary — bordered panel, subtle inset fill */
.transaction-header-bar {
  background: transparent;
  padding: 16px;
  margin-bottom: 16px;
  border: 1px solid var(--wagers-detail-summary-border);
  border-radius: var(--wagers-modal-info-radius, 8px);
  box-sizing: border-box;
}

.header-info-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.info-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
}

.info-label {
  color: var(--wagers-detail-label-text);
  font-size: 14px;
  font-weight: 500;
  flex-shrink: 0;
}

.info-value {
  color: var(--wagers-detail-value-text);
  font-size: 14px;
  font-weight: var(--wagers-modal-value-weight, 600);
  text-align: right;
}

.info-value-copy {
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  flex-wrap: wrap;
  max-width: 65%;
}

.info-value.reference-wrap {
  word-break: break-all;
}

.copy-button {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px;
  border: none;
  background: transparent;
  color: var(--wagers-detail-value-text);
  cursor: pointer;
  opacity: 0.85;
}

.copy-button:hover {
  opacity: 1;
}

.status-text.open,
.draw-status-text.open,
.bet-status-label.open {
  color: var(--wagers-detail-status-open);
}

.status-text.settled,
.status-text.won,
.draw-status-text.settled,
.draw-status-text.won,
.bet-status-label.settled,
.bet-status-label.won {
  color: var(--wagers-detail-status-won);
}

.status-text.lost,
.draw-status-text.lost,
.bet-status-label.lost {
  color: var(--wagers-detail-status-lost);
}

.status-text.cancelled,
.draw-status-text.cancelled,
.bet-status-label.cancelled {
  color: var(--wagers-detail-status-cancelled);
}

.draw-status-text {
  font-size: 14px;
  font-weight: 600;
  flex-shrink: 0;
}

/* Draws section */
.draws-section {
  padding-top: 16px;
}

.draws-section-title {
  margin: 0 0 12px;
  font-size: 14px;
  font-weight: 700;
  color: var(--wagers-detail-value-text);
}

.game-draw-section {
  background: var(--wagers-detail-panel-bg);
  border: 1px solid var(--wagers-detail-border);
  border-radius: var(--wagers-card-radius, 8px);
  margin-bottom: 12px;
  overflow: hidden;
}

.game-draw-section:last-child {
  margin-bottom: 0;
}

.draw-header-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  background: var(--wagers-detail-band-bg);
  padding: 12px 16px;
}

.draw-header-main {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  flex: 1;
}

.draw-index-badge {
  flex-shrink: 0;
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 23px;
  height: 27px;
  padding: 3px 8px 2px;
  border-radius: 4px;
  background: var(--wagers-detail-chip-bg);
  border: none;
  font-size: 14px;
  font-weight: 600;
  line-height: 1;
  color: var(--wagers-detail-value-text);
}

.game-draw-title {
  font-size: 14px;
  font-weight: 600;
  color: var(--wagers-detail-draw-title-text);
  margin: 0;
  line-height: 1.35;
  min-width: 0;
}

.draw-summary-rows {
  padding: 0;
  background: var(--wagers-detail-panel-bg);
}

.summary-row-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid var(--wagers-detail-border);
}

.summary-label {
  font-size: 14px;
  font-weight: 500;
  color: var(--wagers-detail-label-text);
}

.summary-value {
  font-size: 14px;
  font-weight: var(--wagers-modal-summary-value-weight, 600);
  color: var(--wagers-detail-value-text);
  text-align: right;
}

.results-numbers-inline {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  justify-content: flex-end;
}

.no-results-text {
  font-weight: 600;
  color: var(--wagers-detail-value-text);
}

.bets-accordion-trigger {
  width: 100%;
  margin: 0;
  border: none;
  border-bottom: 1px solid var(--wagers-detail-border);
  background: var(--wagers-detail-panel-bg);
  font: inherit;
  text-align: left;
  cursor: pointer;
}

.bets-accordion-label {
  flex: 1;
  text-align: left;
}

.bets-accordion-trigger:hover {
  opacity: 0.92;
}

.bets-accordion-trigger .accordion-chevron {
  color: var(--wagers-detail-value-text);
  opacity: 0.85;
}

.accordion-chevron {
  flex-shrink: 0;
  transition: transform 0.2s ease;
}

.accordion-chevron--expanded {
  transform: rotate(-180deg);
}

.bets-table-container {
  padding: 0;
  overflow-x: auto;
  background: transparent;
}

.desktop-only {
  display: block;
}

.mobile-only {
  display: none;
}

.bets-data-table {
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;
}

.bets-data-table col {
  width: 12.5%;
}

.bets-data-table th {
  background: var(--wagers-detail-table-header-bg);
  color: var(--wagers-detail-table-header-text);
  font-size: 12px;
  font-weight: 700;
  padding: 10px 8px;
  border-bottom: 1px solid var(--wagers-detail-border);
  white-space: normal;
  line-height: 1.25;
}

.bets-data-table th.col-text,
.bets-data-table td.col-text {
  text-align: left;
}

.bets-data-table th.col-numeric,
.bets-data-table td.col-numeric {
  text-align: right;
}

.bets-data-table th.col-status,
.bets-data-table td.col-status {
  text-align: center;
}

.bets-data-table th,
.bets-data-table td {
  overflow-wrap: anywhere;
  word-break: break-word;
}

.bets-data-table td {
  font-size: 13px;
  color: var(--wagers-detail-value-text);
  border-bottom: 1px solid var(--wagers-detail-border);
  vertical-align: top;
}

.bet-id-cell,
.bet-card-header {
  display: flex;
  align-items: center;
  gap: 10px;
}

.bet-letter-badge {
  min-width: 23px;
}

.bet-id-text {
  font-size: 13px;
  font-weight: 700;
  color: var(--wagers-detail-value-text);
  white-space: nowrap;
}

.bet-group + .bet-group .bet-group-label-row .bet-group-label-cell {
  border-top: 1px solid var(--wagers-detail-border);
}

.bet-group-label-row .bet-group-label-cell {
  padding: 4px 16px;
  background: var(--wagers-detail-band-bg);
  border-bottom: 1px solid var(--wagers-detail-border);
  vertical-align: middle;
}

.bet-group-header-row th {
  background: var(--wagers-detail-table-header-bg);
  color: var(--wagers-detail-table-header-text);
  font-size: 12px;
  font-weight: 700;
  border-bottom: 1px solid var(--wagers-detail-border);
  white-space: normal;
  line-height: 1.25;
  hyphens: auto;
}

.bet-group-data-row td {
  border-bottom: 1px solid var(--wagers-detail-border);
}

.bet-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 4px 12px;
  background: var(--wagers-detail-band-bg);
  border-bottom: 1px solid var(--wagers-detail-border);
}

.bet-card-status {
  flex-shrink: 0;
  font-size: 13px;
  font-weight: 700;
  text-transform: capitalize;
}

.bet-card-status.open {
  color: var(--wagers-detail-status-open);
}

.bet-card-status.settled,
.bet-card-status.won {
  color: var(--wagers-detail-status-won);
}

.bet-card-status.lost {
  color: var(--wagers-detail-status-lost);
}

.bet-card-status.cancelled {
  color: var(--wagers-detail-status-cancelled);
}

.bet-details-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-bottom: 8px;
}

.market-name-label {
  font-weight: 700;
  display: block;
  color: var(--wagers-detail-value-text);
}

.market-text-label {
  font-size: 12px;
  color: var(--wagers-detail-label-text);
}

.selected-numbers-row {
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
}

.number-ball-item, .number-ball-small {
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  background: var(--wagers-detail-number-ball-bg);
  border: 1px solid var(--wagers-detail-number-ball-border);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
  color: var(--wagers-detail-number-ball-text);
}

.number-ball-item.bonus, .number-ball-small.bonus {
  background: var(--wagers-detail-bonus-ball-bg);
  border-color: var(--wagers-detail-bonus-ball-bg);
  color: var(--wagers-detail-bonus-ball-text);
}

.bet-status-label {
  font-weight: 700;
  text-transform: capitalize;
}

.font-bold { font-weight: var(--wagers-modal-bold-value-weight, 700); }

.event-cancelled-badge {
  background: var(--wagers-detail-status-cancelled);
  color: var(--colour-text-inverse);
  font-size: 10px;
  padding: 2px 6px;
  border-radius: 4px;
  margin-left: 8px;
  vertical-align: middle;
}

.details-footer {
  display: flex;
  justify-content: center;
  padding: 24px 0 0;
  margin-top: 16px;
  background: transparent;
  border-top: 1px solid var(--wagers-detail-border);
}

.footer-rebet-button {
  background: var(--wagers-detail-rebet-bg);
  color: var(--wagers-detail-rebet-text);
  border: none;
  padding: 14px 32px;
  border-radius: var(--wagers-rebet-button-radius, 8px);
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.2s;
  width: 100%;
  max-width: 300px;
}

.footer-rebet-button:hover {
  opacity: 0.9;
}

.footer-rebet-button:disabled {
  cursor: wait;
  opacity: 0.65;
}

@media (min-width: 769px) {
  .game-name-heading {
    font-size: 18px;
  }

  .header-info-list {
    gap: 10px;
  }

  /* Desktop bet table — equal columns; header aligns with cells below */
  .bets-table-container .bets-data-table th,
  .bets-table-container .bets-data-table td {
    text-align: center;
    vertical-align: top;
    padding: 10px 6px;
    box-sizing: border-box;
  }

  .bets-table-container .bet-details-info {
    align-items: center;
    text-align: center;
  }

  .bets-table-container .selected-numbers-row {
    justify-content: center;
  }
}

@media (max-width: 768px) {
  .desktop-only { display: none; }
  .mobile-only { display: block; }

  .details-content {
    padding: 10px;
  }

  .game-name-heading {
    padding: 0;
    margin-bottom: 12px;
  }

  .transaction-header-bar {
    margin: 0 0 12px;
    padding: 12px;
  }

  .header-info-list {
    gap: 6px;
  }

  .info-label,
  .info-value {
    font-size: 13px;
  }

  .info-value-copy {
    max-width: 58%;
  }

  .draws-section {
    padding-top: 12px;
  }

  .draws-section-title {
    padding: 0;
  }

  .game-draw-section {
    border-radius: var(--wagers-card-radius, 8px);
    margin-bottom: 8px;
  }

  .bets-table-container {
    padding: 0 0 4px;
  }

  .bet-item-card-mobile {
    margin: 8px 10px 10px;
    border: 1px solid var(--wagers-detail-border);
    border-radius: var(--wagers-card-radius, 8px);
    overflow: hidden;
    background: var(--wagers-detail-nested-bet-bg);
  }

  .bet-item-card-mobile:first-child {
    margin-top: 10px;
  }

  .draw-header-bar {
    padding: 8px 12px;
  }

  .game-draw-title {
    font-size: 13px;
  }

  .summary-row-item {
    align-items: flex-start;
    gap: 12px;
    padding: 8px 12px;
  }

  .summary-label,
  .summary-value {
    font-size: 13px;
  }

  .summary-row-item.bets-accordion-trigger {
    padding: 8px 12px;
  }

  .bets-accordion-label {
    font-size: 13px;
  }

  .bet-row-item {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 12px;
    padding: 8px 12px;
    border-bottom: 1px solid var(--wagers-detail-border);
  }

  .bet-row-item:last-child {
    border-bottom: none;
  }

  .bet-row-item .label {
    font-size: 13px;
    color: var(--wagers-detail-label-text);
    font-weight: 500;
    flex-shrink: 0;
  }

  .bet-row-item .value {
    font-size: 13px;
    font-weight: 600;
    color: var(--wagers-detail-value-text);
    text-align: right;
  }

  .bet-outcome-value {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 4px;
    max-width: 65%;
  }

  .footer-rebet-button {
    max-width: none;
    margin: 0;
    width: 100%;
    padding: 12px 16px;
  }

  .details-footer {
    padding: 12px 0;
    margin-top: 0;
  }
}
</style>
